import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.scss";

import cx from "classnames";
import { format, parse } from "date-fns";
import isNaN from "lodash/isNaN";
import { forwardRef, useRef, useState } from "react";
import DatePickerPrimative from "react-datepicker";
import type { FieldErrors } from "react-hook-form";
import MaskedInput from "react-maskedinput";
import { DATE_FORMAT, DATE_PLACEHOLDER } from "utils/date-utils";

export type DatePickerProps = {
  label?: string;
  name: string;
  initialDate?: Date;
  onDateChange?: (date: Date | null) => void;
  onBlur?: () => void;
  className?: string;
  style?: React.CSSProperties;
  errors?: FieldErrors;
  allowFutureDates?: boolean;
  readOnly?: boolean | (() => boolean);
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isFocused?: boolean;
  isClearable?: boolean;
};

const DatePicker = forwardRef<DatePickerPrimative, DatePickerProps>(
  (
    {
      label,
      name,
      initialDate,
      onDateChange,
      onBlur,
      className = "",
      style,
      errors = null,
      required = false,
      allowFutureDates = false,
      readOnly,
      minDate = new Date("1900-01-01"),
      maxDate,
      isFocused,
      isClearable = true,
    },
    ref,
  ) => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(
      initialDate || null,
    );
    const datePickerRef = useRef<DatePickerPrimative>(null);

    const handleMaskedInputChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const inputValue = event.target.value;
      const parsedDate = parse(inputValue, "MM/dd/yyyy", new Date());

      if (!isNaN(parsedDate.getTime())) {
        setSelectedDate(parsedDate);
        onDateChange?.(parsedDate);
      } else if (!inputValue) {
        setSelectedDate(null);
        onDateChange?.(null);
      }
    };

    const resolvedReadOnly =
      // @ts-expect-error - readOnly is a function or a boolean
      typeof readOnly === "function" ? readOnly(initialDate) : readOnly;
    return (
      <div
        className={cx("datepicker", className)}
        style={style}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            datePickerRef.current?.setOpen(false);
            onBlur?.();
          }
        }}
      >
        {label && (
          <label htmlFor={name}>
            {label}
            {required && <span className="required">*</span>}
          </label>
        )}
        <DatePickerPrimative
          id={name}
          disabled={resolvedReadOnly}
          name={name}
          className={cx("datepicker__Input", {
            "datepicker__Input--Error": errors && errors?.[name],
            "datepicker__Input--disabled": resolvedReadOnly,
            "datepicker__Input--Focused": isFocused,
          })}
          selected={selectedDate}
          onChange={(date: Date | null) => {
            setSelectedDate(date);
            onDateChange?.(date);
          }}
          onBlur={onBlur}
          placeholderText={DATE_PLACEHOLDER}
          dateFormat={DATE_FORMAT}
          isClearable={!resolvedReadOnly && isClearable}
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={110}
          minDate={minDate}
          maxDate={maxDate || (allowFutureDates ? null : new Date())}
          ref={(r) => {
            // Handle both the forwardRef and local ref
            if (typeof ref === "function") {
              ref(r);
            } else if (ref) {
              ref.current = r;
            }
            datePickerRef.current = r;
          }}
          portalId="datepicker-portal"
          customInput={
            <MaskedInput
              mask="11/11/1111"
              placeholder={DATE_PLACEHOLDER}
              value={selectedDate ? format(selectedDate, "MM/dd/yyyy") : ""}
              onChange={handleMaskedInputChange}
            />
          }
        />
      </div>
    );
  },
);

DatePicker.displayName = "DatePicker";

export default DatePicker;
